// Portfolio Drift & Rebalancing Alpha Auditor -- the feature orchestrator.
//
// Same two-pane layout as the Portfolio Diversification Checker: the input form
// on the left, and on the right either an empty prompt, a processing spinner, or
// the full results dashboard. Unlike the checker (ARQ job + poll) this endpoint
// is synchronous, so a single mutation drives the whole flow.

import React, { useMemo, useState } from 'react';

import { useDriftAudit } from './api';
import { ConversionBanner } from './components/ConversionBanner';
import { DriftAuditorForm } from './components/DriftAuditorForm';
import { DrawdownChart } from './components/DrawdownChart';
import { HoldingDiagnosticsTable } from './components/HoldingDiagnosticsTable';
import { ProRebalancingShowcase } from './components/ProRebalancingShowcase';
import { StrategyComparisonTable } from './components/StrategyComparisonTable';
import { WealthChart } from './components/WealthChart';
import { WeightDriftChart } from './components/WeightDriftChart';
import type { DriftAuditRequest, DriftAuditResponse } from './types';

const ResultsView: React.FC<{ result: DriftAuditResponse }> = ({ result }) => {
    const tickers = useMemo(
        () => result.holding_diagnostics.map((h) => h.ticker),
        [result.holding_diagnostics],
    );
    return (
        <div className="space-y-6">
            <ConversionBanner banner={result.conversion_banner} />

            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-slate-100 text-xs text-slate-500">
                {result.trading_days.toLocaleString('en-US')} aligned trading days
                {` (${result.start_date} to ${result.end_date})`}.
            </div>

            <StrategyComparisonTable rows={result.strategy_comparison} />

            <ProRebalancingShowcase rows={result.strategy_comparison} />

            <WealthChart data={result.timeseries} />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <WeightDriftChart data={result.timeseries} tickers={tickers} />
                <DrawdownChart data={result.timeseries} />
            </div>

            <HoldingDiagnosticsTable rows={result.holding_diagnostics} />
        </div>
    );
};

export const DriftAuditorDashboard: React.FC = () => {
    const audit = useDriftAudit();
    const [result, setResult] = useState<DriftAuditResponse | null>(null);

    const handleSubmit = (payload: DriftAuditRequest) => {
        audit.mutate(payload, { onSuccess: (data) => setResult(data) });
    };

    const isProcessing = audit.isPending;
    const formError = audit.error ?? null;

    return (
        <div className="max-w-7xl mx-auto p-4 lg:p-8 font-sans">
            <div className="text-center mb-10">
                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
                    Portfolio Drift &amp; Rebalancing Alpha Auditor
                </h1>
                <p className="text-slate-600 dark:text-slate-300 max-w-3xl mx-auto text-lg">
                    Left alone, a portfolio quietly drifts away from its target — winners bloat, risk
                    concentrates, and the "volatility tax" silently erodes your compounding. This auditor
                    replays your exact holdings through three rebalancing disciplines and shows the wealth,
                    drawdown and tail-risk cost of doing nothing.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4 h-fit lg:sticky lg:top-8">
                    <DriftAuditorForm onSubmit={handleSubmit} isPending={isProcessing} error={formError} />
                </div>

                <div className="lg:col-span-8 flex flex-col">
                    {isProcessing ? (
                        <div className="h-full min-h-[500px] flex items-center justify-center bg-white rounded-2xl shadow-sm border border-slate-100">
                            <div className="flex flex-col items-center px-4 text-center">
                                <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                <span className="text-slate-700 font-semibold">Running simulations...</span>
                                <span className="text-slate-400 text-sm mt-1">
                                    Replaying your book through drift, calendar and corridor rebalancing.
                                </span>
                            </div>
                        </div>
                    ) : result ? (
                        <ResultsView result={result} />
                    ) : (
                        <div className="h-full min-h-[500px] flex items-center justify-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <p className="text-slate-400 font-medium px-4 text-center text-lg">
                                Enter your target allocation and run the drift audit.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
